/**
 * A step's text with the words someone else just changed glowing in their
 * colour (`--cue`): held, then faded. Under reduced motion the words are
 * tinted and underlined instead, with no animation, for as long as the cue
 * stands.
 *
 * @module components/workflows/canvas/glowText
 */
import { wordDiff, type WordRun } from "@repo/shared/workflowDiff";
import { useLayoutEffect, useMemo, useRef } from "react";

import { type CueMotion, GLOW_MS } from "./revisionCues";

/**
 * `after`'s runs of words, changed or not. A line cut short ends in a word
 * cut with "…", which no longer matches its whole self in `before`: it
 * counts as changed only when no word of `before` starts with it.
 */
export function glowRuns(before: string, after: string): ReadonlyArray<WordRun> {
  if (!after.endsWith("…")) return wordDiff(before, after);
  const cut = after.search(/\S+$/u);
  const stem = after.slice(cut, -1);
  const changed = !before.split(/\s+/u).some((word) => word.startsWith(stem));
  return [...wordDiff(before, after.slice(0, cut)), { text: after.slice(cut), changed }];
}

const HIGHLIGHT: Readonly<Record<CueMotion, string>> = { animate: "canvas-cue-glow", static: "canvas-cue-tint" };

function highlightFor(motion: CueMotion): Highlight | null {
  if (typeof CSS === "undefined" || !("highlights" in CSS)) return null;
  const name = HIGHLIGHT[motion];
  let highlight = CSS.highlights.get(name);
  if (!highlight) {
    highlight = new Highlight();
    CSS.highlights.set(name, highlight);
  }
  return highlight;
}

const FADE: Keyframe[] = [{ "--cue-glow": "32%" }, { "--cue-glow": "32%", offset: 0.5 }, { "--cue-glow": "0%" }];

/** `text`, its words changed since `before` glowing; `glowKey` starts the glow again for a newer change. */
export function GlowText({
  text,
  before,
  glowKey,
  motion,
  className,
}: {
  readonly text: string;
  readonly before: string | undefined;
  readonly glowKey: number | undefined;
  readonly motion: CueMotion;
  readonly className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const runs = useMemo(() => (before === undefined || before === text ? null : glowRuns(before, text)), [before, text]);
  const words = runs?.flatMap((run) => (run.changed ? [run.text] : [])).join(" ") || undefined;

  useLayoutEffect(() => {
    const line = ref.current;
    const node = line?.firstChild;
    const highlight = highlightFor(motion);
    if (!line || !runs || !(node instanceof Text) || !highlight) return;
    const ranges: Range[] = [];
    let at = 0;
    for (const run of runs) {
      if (run.changed) {
        const range = new Range();
        range.setStart(node, at);
        range.setEnd(node, at + run.text.length);
        ranges.push(range);
        highlight.add(range);
      }
      at += run.text.length;
    }
    const fade = motion === "animate" ? line.animate(FADE, { duration: GLOW_MS, easing: "cubic-bezier(0.76, 0, 0.24, 1)", fill: "forwards" }) : null;
    return () => {
      for (const range of ranges) highlight.delete(range);
      fade?.cancel();
    };
  }, [runs, glowKey, motion]);

  return (
    <span ref={ref} className={className} data-workflow-cue-glow={words}>
      {text}
    </span>
  );
}
